import { format, parse } from 'date-fns';
import { get, cloneDeep, upperFirst } from 'lodash';
import { Model, Dimension, Query, Schema, DateRange, DateRangeValue, DynamicRange, QueryFilter, IQueryFilterWrapper } from '@hub-components';
import { DataSliceEditor } from './data-slicer.model';
import { ScheduledDashboard } from './scheduled_dashboard.model';
import { DashboardWidget } from '../modules/dashboards/models/dashboard-widget.model';

export interface GlobalFilter {
  dataset: string
  schema: Schema
  dimensions: Dimension[]
  selection: any
}

export interface Page {
  id: number
  name: string
  widgets: DashboardWidget[]
  showWidgets?: boolean
  hidden?: boolean;
}

export type DateRangeValueJson = { type: 'fixed', start_date: string, end_date: string } | { type: 'dynamic', dynamic: DynamicRange };
export type LegacyDateRangeValueJson = { type: 'dynamic' | 'fixed', value: string, name?: string };

export type Settings<DateRangeValueType> = {
  interval?: DateRangeValueType,
  firstDayOfWeek?: string,
  size?: any,
  backgroundColor?: any,
  gridSize?: any,
};

export interface IGlobalFiltersWrapper {
  version: number;
  data: IQueryFilterWrapper[];
}

export interface DashboardJson {
  account: string
  name: string
  public_uuid: string
  settings: Settings<DateRangeValueJson | LegacyDateRangeValueJson>
  widgets?: any[]
  pages: any[]
  global_filters: IGlobalFiltersWrapper;
  scheduled?: ScheduledDashboard
  data_slicers?: any[];
  data_slicers_attributes?: any;
  data_slicer_ids?: any[];
}

export class Dashboard extends Model {
  id: string;
  name: string;
  account: string;
  owner: string;
  editors: string[];
  updatedAt: Date;
  createdAt: Date;
  settings?: Settings<DateRangeValue> & { legacyInterval?: LegacyDateRangeValueJson };
  permissions: any;
  widgets?: DashboardWidget[];
  publicUuid: string;
  shareUuid: string;
  isTemplate: boolean;
  share: any;
  globalFilters: IGlobalFiltersWrapper;
  scheduled: ScheduledDashboard;
  pages: Page[];
  dataSlicers: DataSliceEditor[];

  constructor(obj: Partial<DashboardJson>, schemas = {}) {
    super(obj);
    if (obj.pages) {
      this.pages = [];
      for (let page of obj.pages) {
        if (page.widgets && schemas) {
          page.widgets = this.createDashboardWidgets(page.widgets, schemas);
        }

        page.hidden = page.hidden ?? false;

        this.pages.push(page);
      }
    } else {
      const widgets = (obj.widgets && schemas) ? this.createDashboardWidgets(obj.widgets, schemas) : [];
      this.pages = [
        {
          id: 0,
          name: 'Page 1',
          widgets: widgets,
          hidden: false,
        } as Page,
      ];
    }
    if (obj.settings) {
      if (obj.settings.interval) {
        const interval = this.parseDateRangeValue(obj.settings.interval);
        if (interval) {
          this.settings.interval = interval;
        } else {
          this.settings.legacyInterval = <LegacyDateRangeValueJson> obj.settings.interval;
          delete this.settings.interval;
        }
      }
    } else {
      this.settings = {};
    }
    if (obj.scheduled) {
      this.scheduled = obj.scheduled;
    }

    if (obj.global_filters) {
      this.globalFilters = this.processFilters(obj.global_filters, schemas);
    }

    this.dataSlicers = (obj.data_slicers || []).map(slice => new DataSliceEditor(slice));
  }

  addWidget(widget: DashboardWidget, page: Page): void {
    this.pages.find(p => p.id === page.id).widgets.push(widget);
  }

  removeWidget(index: number, page: Page): void {
    this.pages.find(p => p.id === page.id).widgets.splice(index, 1);
  }

  bringWidgetToFront(index: number, page: Page): void {
    const foundPage = this.pages.find(p => p.id === page.id);
    foundPage.widgets.push(foundPage.widgets.splice(index, 1)[0]);
  }

  sendWidgetToBack(index: number, page: Page): void {
    const foundPage = this.pages.find(p => p.id === page.id);
    foundPage.widgets.unshift(foundPage.widgets.splice(index, 1)[0]);
  }

  addPage() {
    this.pages = [...this.pages, { id: this.generatePageId(), name: this.generatePageName(), widgets: [], hidden: false, showWidgets: true } as Page];
  }

  duplicatePage(widgets, globalFilters?: IQueryFilterWrapper[], dataSlicers?: DataSliceEditor[]) {
    const newPageId = this.generatePageId();
    this.pages = [...this.pages, { id: newPageId, name: this.generatePageName(), widgets: widgets, showWidgets: true } as Page];
    if (this.globalFilters && globalFilters) {
      const updatedFilters = globalFilters.map(filter => {
        filter.pageId = newPageId;
        return filter;
      });
      this.globalFilters.data = [...updatedFilters, ...this.globalFilters.data];
    }
    if (this.dataSlicers && dataSlicers) {
      const updatedDataSlicers = dataSlicers.map(slice => {
        slice.page = newPageId;
        return slice;
      });
      this.dataSlicers = [...updatedDataSlicers, ...this.dataSlicers];
    }
  }

  removePage(page: Page) {
    this.pages = this.pages.filter(p => p.id !== page.id);
    if (this.globalFilters) {
      this.globalFilters.data = this.globalFilters.data.filter(filter => filter.pageId !== page.id);
    }

    if (this.dataSlicers) {
      this.dataSlicers.forEach(slice => {
        if (slice.page === page.id) {
          slice._destroy = true;
        }
      });
    }
  }

  reorderPages(pages: Page[]) {
    this.pages = pages;
  }

  updatePageVisibility(pageId: number, hidden: boolean) {
    const pageIndex = this.pages.findIndex(p => p.id === pageId);
    this.pages[pageIndex].hidden = hidden;
  }

  asJson(): DashboardJson {
    return {
      account: this.account,
      name: this.name,
      public_uuid: this.publicUuid,
      settings: {
        ...this.settings,
        ...{ interval: this.jsonDateRangeValue(this.settings.interval) },
      },
      pages: this.pages.map(p => {
        return {
          id: p.id,
          name: p.name,
          hidden: p.hidden,
          widgets: p.widgets.map(w => w.asJson()),
        };
      }),
      global_filters: this.formatFilters(this.globalFilters),
      data_slicers_attributes: this.dataSlicers?.map(slice => slice.asJson()),
      data_slicer_ids: this.dataSlicers?.map(slice => slice.id),
    };
  }

  hasGlobalFilters() {
    return this.globalFilters && this.globalFilters.data && this.globalFilters.data.some(filter => !!filter.expressions.length);
  }

  hasMultiplePages() {
    return this.pages.length > 1;
  }

  getCompactedDatasets() {
    let compactedDatasets = new Set();
    for (let page of this.pages) {
      for (let widget of page.widgets) {
        if (widget.query.wasCompacted) {
          compactedDatasets.add(widget.query.schema.displayName);
        }
      }
    }
    return compactedDatasets;
  }

  private createDashboardWidgets(widgets, schemas): DashboardWidget[] {
    return widgets.map(w => {
      let query: Query | undefined;
      if (w.query) {
        const dataset = w.query.dataset;
        if (schemas[dataset]) {
          query = new Query(w.query, schemas[dataset]);
          query.auth = w.query.auth;
        }
      }
      let options = cloneDeep(w.options);
      if (options?.compareTo?.query?.auth) {
        let benchmarkQuery: Query;
        const dataset = options['compareTo']['query']['dataset'];
        if (schemas[dataset]) {
          benchmarkQuery = new Query(options['compareTo']['query'], schemas[dataset]);
          benchmarkQuery.auth = options['compareTo']['query']['auth'];
          options['compareTo']['query'] = benchmarkQuery;
        }
      }
      if (w.type === 'image' && get(options, 'image.imageSource') === 'uploadOrInsertUrl') {
        options['image']['imageSource'] = 'uploadImage';
      }
      return new DashboardWidget(w.type, w.layout, w.inputs, query, options, w.style, w.id);
    });
  }

  private parseDateRangeValue(range: DateRangeValueJson | LegacyDateRangeValueJson): DateRangeValue {
    if ('dynamic' in range) {
      return {
        type: 'dynamic',
        dynamic: range['dynamic'],
      };
    } else if (range.type === 'dynamic') {
      const match = range['value'].match(/^(this|last|yesterday)(?:(?:-(\d+))?-(day|week|month|year)s?)?$/);
      if (match) {
        return {
          type: 'dynamic',
          dynamic: { name: match[1], num: match[2] && +match[2], unit: <any> match[3] },
        };
      }
    } else if ('start_date' in range) {
      const { start_date: startDate, end_date: endDate } = range;
      return {
        type: 'fixed',
        startDate: parse(startDate, 'yyyy-MM-dd', new Date()),
        endDate: parse(endDate, 'yyyy-MM-dd', new Date()),
      };
    } else if (range['value'] && range['value'].includes('-')) {
      const [startDate, endDate] = range['value'].split('-', 2);
      return {
        type: 'fixed',
        startDate: parse(startDate, 'yyyyMMdd', new Date()),
        endDate: parse(endDate, 'yyyyMMdd', new Date()),
      };
    }
  }

  private jsonDateRangeValue(range: DateRangeValue): DateRangeValueJson & LegacyDateRangeValueJson {
    if ('dynamic' in range && range['dynamic']) {
      const { name, num, unit } = range['dynamic'];
      let value = name;
      if (num && num > 1) {
        value += `-${num}`;
      }
      if (unit) {
        value += `-${unit}`;
        if (num && num > 1) {
          value += 's';
        }
      }
      return {
        type: 'dynamic',
        value: value,
        dynamic: range['dynamic'],
        name: upperFirst(value.replace(/-/g, ' ')),
      };
    } else {
      const { startDate, endDate } = <DateRange> range;
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      return {
        type: 'fixed',
        value: `${startDateStr.replace(/-/g, '')}-${endDateStr.replace(/-/g, '')}`,
        start_date: startDateStr,
        end_date: endDateStr,
      };
    }
  }

  private generatePageId() {
    return Math.max.apply(Math, this.pages.map(p => p.id)) + 1;
  }

  private generatePageName() {
    let count = 1;
    const foundPage = this.pages.find(p => p.name === 'New Page');
    if (!foundPage) {
      return 'New Page';
    } else {
      const pageNumber = this.generatePageNumber(count);
      return `New Page (${pageNumber})`;
    }
  }

  private generatePageNumber(count) {
    const foundPage = this.pages.find(p => p.name === `New Page (${count})`);
    if (!foundPage) {
      return count;
    } else {
      count++;
      return this.generatePageNumber(count);
    }
  }

  private processFilters(globalFilters, schemas) {
    if (!globalFilters?.data) {
      return globalFilters;
    }

    const data = globalFilters.data.map((filter: IQueryFilterWrapper) => {
      const filterSchema = schemas[filter.dataset];
      const processedFilter: IQueryFilterWrapper = {
        expressions: filter.expressions.map(expression => {
          if (!expression.dimension && !!filterSchema) {
            const dimension = filterSchema.dimensions.find(dim => dim.name === expression.key);
            return new QueryFilter({ ...expression, dimension });
          } else {
            return new QueryFilter(expression);
          }
        }),
      };
      processedFilter.dataset = filter.dataset;
      processedFilter.pageId = filter.pageId;
      return processedFilter;
    }).filter(filter => filter.expressions.length);
    return { version: globalFilters.version, data };
  }

  private formatFilters(globalFilters): IGlobalFiltersWrapper {
    if (!globalFilters?.data) {
      return globalFilters;
    }

    const data = globalFilters.data.map((filter: IQueryFilterWrapper) => {
      return { ...filter, expressions: filter.expressions.map(expression => expression.asJson()) };
    });

    return { version: globalFilters.version, data };
  }
}
